import type { Client, DebtType } from '@/types';

/**
 * Sentinel debt-type id for the synthetic "Others" cohort — cases that belong
 * to the client but have `debt_type_id IS NULL`. It is not a row in the
 * `debt_types` table; consumers translate it into an `IS NULL` predicate.
 */
export const NO_DEBT_TYPE_ID = '__no_debt_type__';

/** True when the selected debt type is the synthetic "Others" cohort. */
export function isNoDebtTypeCohort(debtType: Pick<DebtType, 'id'> | null): boolean {
  return debtType?.id === NO_DEBT_TYPE_ID;
}

/**
 * Build the synthetic "Others" debt type for a client so it can be selected and
 * scoped exactly like a real debt type. Its filter predicate is
 * `debt_type_id IS NULL` (see {@link isNoDebtTypeCohort}).
 */
export function makeNoDebtTypeCohort(client: Client): DebtType {
  return {
    id: NO_DEBT_TYPE_ID,
    client_id: client.id,
    operator_id: client.operator_id,
    name: 'Others',
    description: 'Cases without debt type',
    status: 'active',
    metadata: {},
    created_at: null,
    updated_at: null,
  } as DebtType;
}

/**
 * The entity a compiled-config read should be keyed by, most specific first.
 *
 * The "Others" cohort is skipped: it stands for `debt_type_id IS NULL` rather than an
 * entity, and those cases resolve to the client's config through the inheritance chain.
 */
export function configEntityId(
  debtType: Pick<DebtType, 'id'> | null | undefined,
  client: Pick<Client, 'id'> | null | undefined,
  operatorId: string
): string {
  if (debtType && !isNoDebtTypeCohort(debtType)) return debtType.id;
  return client?.id ?? operatorId;
}
